import { type MenuConnectorParams } from "instantsearch.js/es/connectors/menu/connectMenu";
import { capitalize } from "lodash";
import { useRouter } from "next/compat/router";
import { usePathname } from "next/navigation";
import { useCallback } from "react";
import { useClearRefinements, useCurrentRefinements } from "react-instantsearch";

//---------------------------------------------------------------------------------
//
// Menu item utils
//
//---------------------------------------------------------------------------------

export const capitalizeLabel: MenuConnectorParams["transformItems"] = (items) => {
  return items.map((item) => ({ ...item, label: capitalize(item.label) }));
};

const experienceLevelOrders: Record<string, number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2,
};

/**
 * Custom sort function to keep the `experienceLevel` items are in the desired order.
 * Expects the values of `beginner`, `intermediate`, and `advanced`.
 */
export const sortExperienceLevels: MenuConnectorParams["sortBy"] = (a, b) => {
  return experienceLevelOrders[a.escapedValue] - experienceLevelOrders[b.escapedValue];
};

//---------------------------------------------------------------------------------
//
// Filter utils
//
//---------------------------------------------------------------------------------

type AlgoliaFilterContext = {
  currentFilter?: string;
  clearFilter: () => Promise<void>;
};

/**
 * Returns the currently selected value for the given attribute, if there is one,
 * and a callback to clear the selected value.
 */
export function useAlgoliaFilterContext(attribute: string): AlgoliaFilterContext {
  const { items } = useCurrentRefinements({ includedAttributes: [attribute] });
  const { refine } = useClearRefinements({ includedAttributes: [attribute] });
  const { clearSearchParameter } = useAlgoliaSearchParameters(attribute);
  const currentFilter = items[0]?.refinements?.[0]?.label;

  const clearFilter = useCallback(async () => {
    await clearSearchParameter();
    refine();
  }, [refine, clearSearchParameter]);

  return {
    currentFilter,
    clearFilter,
  };
}

//---------------------------------------------------------------------------------
//
// Routing utils
//
//---------------------------------------------------------------------------------

/**
 * A map to convert some Algolia attribute names to more user-friendly strings for routing.
 */
const searchParameterNameMap = new Map<string, string>([
  ["categories.name", "category"],
  ["industry.name", "industry"],
  ["team.name", "team"],
]);

/**
 * Returns a modified, user-friendly search parameter name if available,
 * otherwise will just return back the same string.
 */
function getFriendlyParameterName(attribute: string): string {
  return searchParameterNameMap.get(attribute) ?? attribute;
}

type AlgoliaURLSearchParameters = {
  getSearchParameter: () => string | undefined;
  setSearchParameter: (value: string) => Promise<void>;
  clearSearchParameter: () => Promise<void>;
};

// NOTE: Avoid using `useSearchParams` from Next for this, since it takes several additional renders before
// that hook returns the actual search params from the URL when compared to accessing them directly from `window.location`.
// Since all of the calls to access `window` are in `useCallback` hooks, there is no issue using this with Algolia's SSR.
/**
 * Returns a set of callbacks that can be used to read and modify the URL search parameter for a given attribute.
 */
export function useAlgoliaSearchParameters(attribute: string): AlgoliaURLSearchParameters {
  // Uses the compat router to support rendering the serverState for Algolia (which is outside the Next app)
  const router = useRouter();
  const pathname = usePathname();
  const parameterName = getFriendlyParameterName(attribute);

  const getSearchParameter = useCallback(() => {
    const searchParameters = new URLSearchParams(window.location.search);
    return searchParameters.get(parameterName) ?? undefined;
  }, [parameterName]);

  // Callback to set a new value
  const setSearchParameter = useCallback(
    async (value: string) => {
      const searchParameters = new URLSearchParams(window.location.search);
      searchParameters.set(parameterName, value);
      await router?.push(pathname + "?" + searchParameters.toString(), undefined, { shallow: true });
    },
    [router, pathname, parameterName],
  );

  // Callback to clear the value
  const clearSearchParameter = useCallback(async () => {
    const searchParameters = new URLSearchParams(window.location.search);
    searchParameters.delete(parameterName);

    const searchString = searchParameters.toString();
    const newSearch = searchString ? "?" + searchString : "";
    await router?.push(pathname + newSearch, undefined, { shallow: true });
  }, [router, pathname, parameterName]);

  return {
    getSearchParameter,
    setSearchParameter,
    clearSearchParameter,
  };
}
